import type { MapSchema } from "@colyseus/schema";
import { type Room, getStateCallbacks } from "@colyseus/sdk";
import { NETWORK, type EnemyView, type PlayerView } from "@mucki/shared";
import * as THREE from "three";
import { type RemoteEnemy, createRemoteEnemy } from "../entities/enemyMesh.js";
import { type RemotePlayer, createRemotePlayer } from "../entities/remotePlayer.js";

/** The room state as the client decodes it. */
export interface ZoneState {
  players: MapSchema<PlayerView>;
  enemies: MapSchema<EnemyView>;
}

export type ZoneRoom = Room<any, ZoneState>;

export interface StateSync {
  update(delta: number, camera: THREE.Camera, canvas: HTMLCanvasElement): void;
  localPlayer(): PlayerView | undefined;
  enemies(): Iterable<EnemyView>;
  findEnemy(id: string): EnemyView | undefined;
  findPlayer(sessionId: string): PlayerView | undefined;
  dispose(): void;
}

/**
 * Turns room state callbacks into scene objects. Remote cats are added on
 * player add and disposed on player remove, and the local session is skipped
 * because it is rendered by the local controller instead.
 */
export function createStateSync(room: ZoneRoom, scene: THREE.Scene): StateSync {
  const remotes = new Map<string, RemotePlayer>();
  const enemies = new Map<string, RemoteEnemy>();
  const detach: Array<() => void> = [];
  const $ = getStateCallbacks(room);

  detach.push(
    $(room.state).players.onAdd((player, sessionId) => {
      if (sessionId === room.sessionId) return;

      const remote = createRemotePlayer(scene, sessionId, player.name, {
        x: player.x,
        y: player.y,
        z: player.z,
        rotationY: player.rotationY,
      });
      remotes.set(sessionId, remote);

      $(player).onChange(() => {
        remote.applySnapshot(
          { x: player.x, y: player.y, z: player.z, rotationY: player.rotationY },
          Date.now(),
        );
        remote.setName(player.name);
        remote.setAnimationState(player.animation);
      });
    }),
  );

  detach.push(
    $(room.state).players.onRemove((_player, sessionId) => {
      remotes.get(sessionId)?.dispose();
      remotes.delete(sessionId);
    }),
  );

  detach.push(
    $(room.state).enemies.onAdd((enemy, id) => {
      const entity = createRemoteEnemy(scene, {
        x: enemy.x,
        y: enemy.y,
        z: enemy.z,
        rotationY: enemy.rotationY,
      });
      enemies.set(id, entity);

      $(enemy).onChange(() => {
        entity.applySnapshot(
          { x: enemy.x, y: enemy.y, z: enemy.z, rotationY: enemy.rotationY },
          Date.now(),
        );
        entity.setState(enemy.state);
      });
    }),
  );

  detach.push(
    $(room.state).enemies.onRemove((_enemy, id) => {
      enemies.get(id)?.dispose();
      enemies.delete(id);
    }),
  );

  return {
    update: (delta, camera, canvas) => {
      const renderTime = Date.now() - NETWORK.interpolationDelayMs;
      for (const remote of remotes.values()) {
        remote.update(renderTime, delta, camera, canvas);
      }
      for (const enemy of enemies.values()) {
        enemy.update(renderTime, delta);
      }
    },
    localPlayer: () => room.state.players.get(room.sessionId),
    enemies: () => room.state.enemies.values(),
    findEnemy: (id) => room.state.enemies.get(id),
    findPlayer: (sessionId) => room.state.players.get(sessionId),
    dispose: () => {
      for (const off of detach) off();
      for (const remote of remotes.values()) remote.dispose();
      for (const enemy of enemies.values()) enemy.dispose();
      remotes.clear();
      enemies.clear();
    },
  };
}
